import { t } from '@hefe/i18n';
import {
  AssignableType,
  CustomTableColumn,
  Task,
  TaskAutoAssignmentAssignableUser,
} from '@hefe/models';
import { TasksSlice } from '@hefe/store';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import CustomTable from '../../../../core/components/table/custom-table.component';

interface TasksAutoAssignmentWizardUsersComponentProps {
  task: Task;
  basketballTeamId?: number;
}

export default function TasksAutoAssignmentWizardUsersComponent(
  props: TasksAutoAssignmentWizardUsersComponentProps
) {
  const dispatch = useAppDispatch();
  const wizard = useSelector(TasksSlice.autoAssignmentWizardSelector);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(
      TasksSlice.fetchAutoAssignmentAssignables({
        taskId: props.task.id,
        type: wizard.mode,
        basketballTeamId: props.basketballTeamId,
      })
    )
      .unwrap()
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [dispatch, props.basketballTeamId, props.task.id, wizard.mode]);

  const columns = useMemo(() => {
    if (wizard.mode === AssignableType.USER) {
      let playerTeamNames: string[] = [];
      let coachTeamNames: string[] = [];

      (wizard.assignables as TaskAutoAssignmentAssignableUser[]).forEach(
        (user) => {
          playerTeamNames.push(...user.player_teams);
          coachTeamNames.push(...user.coach_teams);
        }
      );

      playerTeamNames = Array.from(new Set(playerTeamNames));
      coachTeamNames = Array.from(new Set(coachTeamNames));

      return [
        {
          key: 'firstname',
          title: t('firstname'),
          default: true,
          dataIndex: 'firstname',
          sortType: 'string',
        },
        {
          key: 'lastname',
          title: t('lastname'),
          default: true,
          dataIndex: 'lastname',
          sortType: 'string',
        },
        {
          key: 'teams',
          title: t('firstname'),
          default: true,
          dataIndex: 'firstname',
          sortType: 'string',
        },
        {
          key: 'roles',
          title: t('roles.role'),
          dataIndex: 'roles',
          default: true,
          filters: [
            { value: 'bb_admin', text: t('roles.bb_admin') },
            { value: 'bb_coach', text: t('roles.bb_coach') },
            { value: 'bb_referee', text: t('roles.bb_referee') },
          ],
          onFilter: (value: string, user: TaskAutoAssignmentAssignableUser) => {
            return user.roles.includes(value);
          },
          render: (roles: string[]) =>
            roles.map((role) => t(`roles.${role}`)).join(', '),
        },
        {
          key: 'player_teams',
          title: `${t('teams')} (${t('player')})`,
          sortType: 'string',
          default: true,
          filters: playerTeamNames.map((name) => ({ value: name, text: name })),
          onFilter: (value: string, user: TaskAutoAssignmentAssignableUser) => {
            return user.player_teams.includes(value);
          },
          render: (user: TaskAutoAssignmentAssignableUser) => {
            return user.player_teams?.join(', ');
          },
        },
        {
          key: 'coached_teams',
          title: `${t('teams')} (${t('coach')})`,
          sortType: 'string',
          default: true,
          filters: coachTeamNames.map((name) => ({ value: name, text: name })),
          onFilter: (value: string, user: TaskAutoAssignmentAssignableUser) => {
            return user.coach_teams.includes(value);
          },
          render: (user: TaskAutoAssignmentAssignableUser) => {
            return user.coach_teams?.join(', ');
          },
        },
      ] as CustomTableColumn[];
    } else if (wizard.mode === AssignableType.BASKETBALL_TEAM) {
      return [
        {
          key: 'name',
          title: t('name'),
          default: true,
          dataIndex: 'name',
          sortType: 'string',
        },
      ] as CustomTableColumn[];
    }

    return [];
  }, [wizard.mode, wizard.assignables]);

  return (
    <CustomTable
      name="tasks-auto-assignment-wizard-users-table"
      key="tasks-auto-assignment-wizard-users-table"
      rowKey="id"
      hideColumnSelector
      rowSelection
      onRowSelect={(selectedRowKeys) =>
        dispatch(TasksSlice.actions.setWizardAssignablesIds(selectedRowKeys))
      }
      initSelectedRowKeys={wizard.assignableIds}
      selectedRowKeys={wizard.assignableIds}
      columns={columns}
      dataSource={wizard.assignables}
      loading={loading}
    />
  );
}
